'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { 
  FiUpload, FiFolder, FiFile, FiTrash2, FiDownload, FiArrowLeft, 
  FiFileText, FiCloudLightning, FiEdit2, FiMove, FiCopy, FiLock,
  FiMoreVertical, FiCheckSquare, FiSquare, FiArchive
} from 'react-icons/fi'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/ui-dialog"
import { cn } from '@/lib/utils'
import Swal from 'sweetalert2'

export default function FileManagerPage() {
  const [files, setFiles] = useState<any[]>([])
  const [currentPath, setCurrentPath] = useState('')
  const [account, setAccount] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [loadingAction, setLoadingAction] = useState(false)
  
  // Selection State
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
  
  // Context Menu State
  const [contextMenu, setContextMenu] = useState<{ x: number, y: number, item: any } | null>(null)
  
  // Modal States
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false)
  const [isCompressModalOpen, setIsCompressModalOpen] = useState(false)
  
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [overwrite, setOverwrite] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Editor State
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const [editingFile, setEditingFile] = useState<{ name: string, content: string } | null>(null)
  const [saving, setSaving] = useState(false)

  // Action Inputs
  const [newName, setNewName] = useState('')
  const [zipName, setZipName] = useState('')
  const [actionItem, setActionItem] = useState<any>(null)

  useEffect(() => {
    fetchUserData()
    
    const handleClick = () => setContextMenu(null)
    window.addEventListener('click', handleClick)
    return () => window.removeEventListener('click', handleClick)
  }, [])

  useEffect(() => {
    if (account) {
      fetchFiles(account.folderName || account.folder_name)
    }
    // Limpiar selección al cambiar de carpeta
    setSelectedItems(new Set())
  }, [currentPath, account])

  const fetchUserData = async () => {
    try {
      const res = await fetch('/api/auth/verificar')
      const data = await res.json()
      if (data.success) {
        const savedAccId = localStorage.getItem('selected_hosting_account')
        const accounts = data.usuario.hostingAccounts || []
        const acc = accounts.find((a: any) => a.id.toString() === savedAccId) || accounts[0]
        if (acc) setAccount(acc)
        else setLoading(false)
      } else {
        setLoading(false)
      }
    } catch (error) {
      console.error('Error fetching user data:', error)
      setLoading(false)
    }
  }

  const fetchFiles = async (folder: string, silent = false) => {
    if (!folder) return
    if (!silent) setLoading(true)
    try {
      const res = await fetch(`/api/hosting/files?folder=${encodeURIComponent(folder)}&path=${encodeURIComponent(currentPath)}`)
      const data = await res.json()
      setFiles(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error fetching files:', error)
    }
    if (!silent) setLoading(false)
  }

  const handleAction = async (action: string, data: any) => {
    const folder = account?.folderName || account?.folder_name
    if (!folder) return

    try {
      const res = await fetch('/api/hosting/files', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
            action, 
            folder, 
            path: currentPath,
            ...data 
        })
      })
      const result = await res.json()
      if (result.success) {
        fetchFiles(folder, true)
        Swal.fire({
          title: '¡Logrado!',
          text: result.message || 'Operación completada con éxito',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        })
        return true
      } else {
        Swal.fire('Error', result.error || 'Operación fallida', 'error')
        return false
      }
    } catch (e) {
      Swal.fire('Error', 'Error de conexión', 'error')
      return false
    }
  }

  const toggleSelect = (fileName: string) => {
    setSelectedItems(prev => {
      const next = new Set(prev)
      if (next.has(fileName)) next.delete(fileName)
      else next.add(fileName)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedItems.size === files.length) setSelectedItems(new Set())
    else setSelectedItems(new Set(files.map(f => f.name)))
  }

  const handleContextMenu = (e: React.MouseEvent, item: any) => {
    e.preventDefault()
    setContextMenu({ x: e.clientX, y: e.clientY, item })
    // Si no está seleccionado, seleccionarlo y limpiar otros si no se presiona ctrl
    if (!selectedItems.has(item.name)) {
        setSelectedItems(new Set([item.name]))
    }
  }

  const handleDeleteBulk = async () => {
    const folder = account?.folderName || account?.folder_name
    if (!folder || selectedItems.size === 0) return

    const result = await Swal.fire({
      title: '¿Eliminar elementos?',
      text: `Estás a punto de borrar ${selectedItems.size} elementos de forma permanente.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    })

    if (result.isConfirmed) {
      setLoadingAction(true)
      const errors: string[] = []
      
      for (const itemName of Array.from(selectedItems)) {
        try {
          const filePath = currentPath ? `${currentPath}/${itemName}` : itemName
          const res = await fetch(`/api/hosting/files?folder=${encodeURIComponent(folder)}&path=${encodeURIComponent(filePath)}`, { method: 'DELETE' })
          const data = await res.json()
          if (!res.ok) {
            errors.push(`${itemName}: ${data.error || 'Error desconocido'}`)
          }
        } catch (e: any) {
          errors.push(`${itemName}: ${e.message || 'Error de red'}`)
        }
      }
      
      setLoadingAction(false)
      setSelectedItems(new Set())
      fetchFiles(folder, true)
      
      if (errors.length > 0) {
        Swal.fire({
          title: 'Algunos elementos no se borraron',
          html: `<div class="text-left text-xs text-red-500 mt-2">${errors.join('<br>')}</div>`,
          icon: 'warning',
          confirmButtonColor: '#3b82f6'
        })
      } else {
        Swal.fire({
          title: '¡Eliminado!',
          text: 'Los elementos han sido borrados con éxito.',
          icon: 'success',
          confirmButtonColor: '#3b82f6'
        })
      }
    }
  }

  const handleRename = async () => {
    if (!newName || !actionItem) return
    const items = [currentPath ? `${currentPath}/${actionItem.name}` : actionItem.name]
    const success = await handleAction('rename', { items, name: newName })
    if (success) setIsRenameModalOpen(false)
  }

  const handleCompress = async () => {
    if (!zipName) return
    const items = Array.from(selectedItems).map(name => currentPath ? `${currentPath}/${name}` : name)
    const success = await handleAction('compress', { items, name: zipName })
    if (success) {
        setIsCompressModalOpen(false)
        setSelectedItems(new Set())
    }
  }

  const handleDownload = async (fileName: string) => {
    const folder = account?.folderName || account?.folder_name
    if (!folder) return
    const filePath = currentPath ? `${currentPath}/${fileName}` : fileName
    window.open(`/api/hosting/files?folder=${encodeURIComponent(folder)}&path=${encodeURIComponent(filePath)}`, '_blank')
  }

  const navigateTo = (dirName: string) => {
    setCurrentPath(prev => prev ? `${prev}/${dirName}` : dirName)
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true)
    else if (e.type === "dragleave") setDragActive(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUpload(e.dataTransfer.files[0])
    }
  }

  const goBack = () => {
    const parts = currentPath.split('/')
    parts.pop()
    setCurrentPath(parts.join('/'))
  }

  const handleUpload = async (file: File) => {
    const folder = account?.folderName || account?.folder_name
    if (!file || !folder) return
    setUploading(true)
    setUploadProgress(0)

    const formData = new FormData()
    formData.append('file', file)
    formData.append('folder', folder)
    formData.append('path', currentPath)
    formData.append('extract', 'true')

    const interval = setInterval(() => {
      setUploadProgress(prev => (prev < 90 ? prev + Math.random() * 10 : prev))
    }, 300)

    try {
      const res = await fetch('/api/hosting/files', { method: 'POST', body: formData })
      clearInterval(interval)
      setUploadProgress(100)
      if (res.ok) {
        setTimeout(() => {
          setIsUploadModalOpen(false)
          setUploading(false)
          fetchFiles(folder)
          Swal.fire({ title: '¡Subido!', icon: 'success', toast: true, position: 'top-end', timer: 2000, showConfirmButton: false })
        }, 500)
      } else {
        const err = await res.json()
        Swal.fire('Error', err.error || 'No se pudo subir', 'error')
        setUploading(false)
      }
    } catch (error) {
      Swal.fire('Error', 'Error de red', 'error')
      setUploading(false)
    }
  }

  const handleEdit = async (fileName: string) => {
    if (fileName.toLowerCase().endsWith('.html')) {
        Swal.fire({
            title: 'No editable',
            text: 'Los archivos .html no se pueden visualizar ni editar directamente en este entorno debido a bloqueos de seguridad de Laragon/Apache.',
            icon: 'info',
            confirmButtonColor: '#3b82f6'
        })
        return
    }

    const folder = account?.folderName || account?.folder_name
    if (!folder) return
    setLoadingAction(true)
    try {
      const filePath = currentPath ? `${currentPath}/${fileName}` : fileName
      const res = await fetch(`/api/hosting/files?folder=${encodeURIComponent(folder)}&path=${encodeURIComponent(filePath)}&preview=1`)
      const data = await res.json()
      
      if (res.ok && data.content !== undefined) {
        setEditingFile({ name: fileName, content: data.content })
        setIsEditorOpen(true)
      } else {
        Swal.fire('Error', data.error || 'No se pudo leer el archivo', 'error')
      }
    } catch (err) {
      Swal.fire('Error', 'Error al abrir el editor', 'error')
    }
    setLoadingAction(false)
  }

  const handleSaveEdit = async () => {
    const folder = account?.folderName || account?.folder_name
    if (!folder || !editingFile) return
    setSaving(true)
    try {
      const filePath = currentPath ? `${currentPath}/${editingFile.name}` : editingFile.name
      const res = await fetch('/api/hosting/files', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folder, path: filePath, content: editingFile.content })
      })
      if (res.ok) {
        setIsEditorOpen(false)
        Swal.fire({ title: '¡Guardado!', icon: 'success', toast: true, position: 'top-end', timer: 2000, showConfirmButton: false })
      }
    } catch (err) {
      Swal.fire('Error', 'Error de red al guardar', 'error')
    }
    setSaving(false)
  }

  const getFileIcon = (fileName: string, isDirectory: boolean) => {
    if (isDirectory) return <FiFolder className="text-blue-500 h-5 w-5" />
    const ext = fileName.split('.').pop()?.toLowerCase()
    switch (ext) {
      case 'php': return <div className="bg-purple-100 p-1.5 rounded-lg"><FiFileText className="text-purple-600 h-4 w-4" /></div>
      case 'html': return <div className="bg-orange-100 p-1.5 rounded-lg"><FiFileText className="text-orange-600 h-4 w-4" /></div>
      case 'js': return <div className="bg-yellow-100 p-1.5 rounded-lg"><FiFileText className="text-yellow-600 h-4 w-4" /></div>
      case 'css': return <div className="bg-blue-100 p-1.5 rounded-lg"><FiFileText className="text-blue-600 h-4 w-4" /></div>
      case 'json': return <div className="bg-green-100 p-1.5 rounded-lg"><FiFileText className="text-green-600 h-4 w-4" /></div>
      case 'zip': return <div className="bg-amber-100 p-1.5 rounded-lg"><FiArchive className="text-amber-600 h-4 w-4" /></div>
      default: return <FiFile className="text-gray-400 h-5 w-5" />
    }
  }

  const sortedFiles = [...files].sort((a, b) => {
    if (a.isDirectory && !b.isDirectory) return -1
    if (!a.isDirectory && b.isDirectory) return 1
    return a.name.localeCompare(b.name)
  })

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20 select-none">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">Administrador de Archivos</h1>
          <div className="flex items-center gap-2 mt-2">
            <div className="flex items-center gap-1.5 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-bold ring-1 ring-blue-100">
              <FiFolder />
              <span className="font-mono">
                {account?.folderName || account?.folder_name || '...'}
                {currentPath ? `/${currentPath}` : ''}
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          {currentPath && (
            <Button variant="outline" onClick={goBack} className="gap-2 rounded-2xl h-11 border-gray-200 font-bold hover:bg-gray-50">
              <FiArrowLeft /> Atrás
            </Button>
          )}
          <Button onClick={() => setIsUploadModalOpen(true)} className="gap-2 bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-100 border-none rounded-2xl h-11 font-bold px-6">
            <FiUpload /> Subir Archivo
          </Button>
        </div>
      </div>

      {/* Toolbar Bulk Actions */}
      {selectedItems.size > 0 && (
        <Card className="border-none shadow-xl bg-indigo-900 text-white p-4 rounded-3xl flex items-center justify-between sticky top-4 z-50 animate-in slide-in-from-top-4 duration-300">
          <div className="flex items-center gap-4 px-2">
            <div className="bg-white/10 p-2 rounded-xl">
               <FiCheckSquare className="text-indigo-200 h-5 w-5" />
            </div>
            <span className="font-black text-sm uppercase tracking-widest">{selectedItems.size} elementos seleccionados</span>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => {
                setZipName(selectedItems.size === 1 ? Array.from(selectedItems)[0].split('.')[0] : 'archive')
                setIsCompressModalOpen(true)
            }} className="gap-2 text-white hover:bg-white/10 rounded-xl font-bold">
              <FiArchive /> Comprimir
            </Button>
            <Button variant="ghost" onClick={handleDeleteBulk} className="gap-2 text-red-300 hover:bg-red-500/20 hover:text-red-200 rounded-xl font-bold">
              <FiTrash2 /> Eliminar
            </Button>
            <div className="w-[1px] bg-white/10 mx-2" />
            <Button variant="ghost" onClick={() => setSelectedItems(new Set())} className="text-indigo-300 hover:text-white rounded-xl">Cerrar</Button>
          </div>
        </Card>
      )}

      {/* File Table */}
      <Card className="border-none shadow-sm overflow-hidden rounded-[2.5rem] bg-white border border-gray-100">
        <Table className="border-separate border-spacing-0">
          <TableHeader className="bg-gray-50/50">
            <TableRow className="hover:bg-transparent border-none">
              <TableHead className="w-12 text-center p-5">
                <Checkbox 
                  checked={selectedItems.size === files.length && files.length > 0} 
                  onCheckedChange={toggleSelectAll}
                  className="rounded-md border-gray-300 data-[state=checked]:bg-blue-600"
                />
              </TableHead>
              <TableHead className="font-black text-gray-400 text-[11px] uppercase tracking-widest p-5">Nombre</TableHead>
              <TableHead className="font-black text-gray-400 text-[11px] uppercase tracking-widest p-5">Tamaño</TableHead>
              <TableHead className="font-black text-gray-400 text-[11px] uppercase tracking-widest p-5">Modificado</TableHead>
              <TableHead className="font-black text-gray-400 text-[11px] uppercase tracking-widest p-5 text-right px-8">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && files.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="py-20 text-center text-gray-400 font-medium">
                <div className="flex flex-col items-center gap-2">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
                    <span>Cargando archivos...</span>
                </div>
              </TableCell></TableRow>
            ) : sortedFiles.map((file, i) => (
              <TableRow 
                key={i} 
                onContextMenu={(e) => handleContextMenu(e, file)}
                className={cn(
                    "hover:bg-blue-50/30 border-b border-gray-50 transition-all duration-200 cursor-default group",
                    selectedItems.has(file.name) && "bg-blue-50/60"
                )}
              >
                <TableCell className="text-center p-4">
                  <Checkbox 
                    checked={selectedItems.has(file.name)} 
                    onCheckedChange={() => toggleSelect(file.name)}
                    className="rounded-md border-gray-200 group-hover:border-blue-400 data-[state=checked]:bg-blue-600"
                  />
                </TableCell>
                <TableCell className="p-4">
                  <div className="flex items-center gap-4">
                    {getFileIcon(file.name, file.isDirectory)}
                    <button 
                        onClick={() => file.isDirectory ? navigateTo(file.name) : handleEdit(file.name)}
                        className="text-sm font-bold text-gray-700 hover:text-blue-600 transition-colors truncate max-w-[400px]"
                    >
                      {file.name}
                    </button>
                    {!file.isDirectory && !file.name.toLowerCase().endsWith('.html') && (
                        <span className="text-[9px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-black uppercase opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">Editable</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-xs font-mono text-gray-400 p-4">
                  {file.isDirectory ? '--' : `${(file.size / 1024).toFixed(1)} KB`}
                </TableCell>
                <TableCell className="text-[11px] font-medium text-gray-400 p-4">
                  {file.mtime}
                </TableCell>
                <TableCell className="text-right p-4 px-8">
                  <button onClick={(e) => { e.stopPropagation(); handleContextMenu(e, file); }} className="p-2 text-gray-300 hover:text-blue-600 transition-colors">
                    <FiMoreVertical />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Context Menu */}
      {contextMenu && contextMenu.item && (
        <div 
          className="fixed z-[100] bg-white border border-gray-100 shadow-2xl rounded-2xl py-2 min-w-[200px] animate-in fade-in zoom-in-95 duration-100 overflow-hidden"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-4 py-2 border-b border-gray-50 mb-1">
             <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest truncate">{contextMenu.item.name}</p>
          </div>
          <button onClick={() => { setActionItem(contextMenu.item); setNewName(contextMenu.item.name); setIsRenameModalOpen(true); setContextMenu(null); }} className="w-full text-left px-4 py-2.5 text-sm font-bold text-gray-600 hover:bg-blue-50 hover:text-blue-600 flex items-center gap-3 transition-colors">
            <FiEdit2 size={14} /> Renombrar
          </button>
          {!contextMenu.item.isDirectory && (
            <>
                <button onClick={() => { handleEdit(contextMenu.item.name); setContextMenu(null); }} className="w-full text-left px-4 py-2.5 text-sm font-bold text-indigo-600 hover:bg-indigo-50 flex items-center gap-3 transition-colors">
                    <FiFileText size={14} /> Editar
                </button>
                <button onClick={() => { handleDownload(contextMenu.item.name); setContextMenu(null); }} className="w-full text-left px-4 py-2.5 text-sm font-bold text-gray-600 hover:bg-blue-50 hover:text-blue-600 flex items-center gap-3 transition-colors">
                    <FiDownload size={14} /> Descargar
                </button>
            </>
          )}
          <button onClick={() => { setZipName(contextMenu.item.name.split('.')[0]); setIsCompressModalOpen(true); setContextMenu(null); }} className="w-full text-left px-4 py-2.5 text-sm font-bold text-gray-600 hover:bg-blue-50 hover:text-blue-600 flex items-center gap-3 transition-colors">
            <FiArchive size={14} /> Comprimir
          </button>
          <div className="h-[1px] bg-gray-50 my-1" />
          <button onClick={() => { handleDeleteBulk(); setContextMenu(null); }} className="w-full text-left px-4 py-2.5 text-sm font-bold text-red-500 hover:bg-red-50 flex items-center gap-3 transition-colors">
            <FiTrash2 size={14} /> Eliminar
          </button>
        </div>
      )}

      {/* ... Modales ... */}

      {/* Modal Rename */}
      <Dialog open={isRenameModalOpen} onOpenChange={setIsRenameModalOpen}>
        <DialogContent className="rounded-[2.5rem] border-none shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-black">Renombrar Elemento</DialogTitle>
            <DialogDescription>Elige un nuevo nombre para <span className="text-blue-600 font-bold">{actionItem?.name}</span></DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <input 
                value={newName} 
                onChange={e => setNewName(e.target.value)}
                className="w-full p-4 rounded-2xl bg-gray-50 border-2 border-transparent focus:border-blue-500 outline-none font-bold text-gray-700 transition-all"
                placeholder="Nombre del archivo..."
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setIsRenameModalOpen(false)} className="rounded-xl">Cancelar</Button>
            <Button onClick={handleRename} className="bg-blue-600 hover:bg-blue-700 rounded-xl font-bold px-8">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Compress */}
      <Dialog open={isCompressModalOpen} onOpenChange={setIsCompressModalOpen}>
        <DialogContent className="rounded-[2.5rem] border-none shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-black">Comprimir Archivos</DialogTitle>
            <DialogDescription>Se creará un archivo ZIP con los elementos seleccionados.</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-amber-50 p-4 rounded-2xl border border-amber-100 mb-4">
                <p className="text-xs text-amber-800 font-medium flex items-center gap-1">
                    <FiArchive /> Elementos: {selectedItems.size || 1}
                </p>
            </div>
            <div className="relative">
                <input 
                    value={zipName} 
                    onChange={e => setZipName(e.target.value)}
                    className="w-full p-4 pr-16 rounded-2xl bg-gray-50 border-2 border-transparent focus:border-blue-500 outline-none font-bold text-gray-700 transition-all"
                    placeholder="nombre_del_archivo"
                />
                <span className="absolute right-5 top-1/2 -translate-y-1/2 font-bold text-gray-400">.zip</span>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setIsCompressModalOpen(false)} className="rounded-xl">Cancelar</Button>
            <Button onClick={handleCompress} className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-bold px-8">Crear ZIP</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ... (Editor de Código se mantiene igual pero con estilo mejorado) ... */}
      <Dialog open={isEditorOpen} onOpenChange={(open) => !saving && setIsEditorOpen(open)}>
        <DialogContent hideClose className="sm:max-w-[1000px] h-[85vh] border-none shadow-2xl p-0 overflow-hidden flex flex-col rounded-[2.5rem]">
          <div className="bg-[#1e1e2e] p-6 text-white flex justify-between items-center border-b border-white/5">
            <div className="flex items-center gap-4">
               <div className="h-10 w-10 rounded-2xl bg-indigo-500 flex items-center justify-center text-xl shadow-lg shadow-indigo-500/30">
                  <FiFileText />
               </div>
               <div>
                  <h3 className="text-md font-black tracking-tight">{editingFile?.name}</h3>
                  <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-0.5">Editor Maestro</p>
               </div>
            </div>
            <div className="flex gap-3">
              <Button variant="ghost" size="sm" onClick={() => setIsEditorOpen(false)} className="text-gray-400 hover:text-white rounded-xl">Cerrar</Button>
              <Button size="sm" onClick={handleSaveEdit} className="bg-indigo-600 hover:bg-indigo-500 text-white font-black px-8 rounded-xl shadow-lg shadow-indigo-500/20" disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </div>
          <div className="flex-1 p-0 bg-[#1e1e2e] overflow-hidden relative">
            <textarea
              className="w-full h-full p-10 bg-transparent text-indigo-100 font-mono text-[13px] outline-none resize-none custom-scrollbar-dark leading-relaxed"
              value={editingFile?.content || ''}
              onChange={(e) => setEditingFile(prev => prev ? { ...prev, content: e.target.value } : null)}
              spellCheck={false}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Upload */}
      <Dialog open={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
        {/* Usamos el DialogContent original o uno similar al anterior pero pulido */}
        <DialogContent className="rounded-[2.5rem] p-10 border-none shadow-2xl">
           <div className="text-center space-y-4">
              <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-[2rem] flex items-center justify-center mx-auto mb-6">
                <FiUpload size={32} />
              </div>
              <DialogTitle className="text-2xl font-black">Subir a /{currentPath || 'root'}</DialogTitle>
              <DialogDescription className="text-sm font-medium text-gray-500">Puedes arrastrar archivos directamente aquí.</DialogDescription>
           </div>
           
           <div 
             className={cn(
               "mt-8 border-2 border-dashed rounded-[2rem] p-12 transition-all text-center",
               dragActive ? "border-blue-500 bg-blue-50" : "border-gray-100 bg-gray-50/50"
             )}
             onDragOver={handleDrag}
             onDrop={handleDrop}
           >
             <input type="file" ref={fileInputRef} className="hidden" onChange={e => e.target.files?.[0] && handleUpload(e.target.files[0])} />
             <Button onClick={() => fileInputRef.current?.click()} className="bg-blue-600 font-bold rounded-xl h-12 px-10">Seleccionar Archivo</Button>
           </div>
           {uploading && (
             <div className="mt-8 space-y-2">
                <div className="h-2 w-full bg-gray-50 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-600 transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                </div>
                <p className="text-center text-[10px] font-black text-blue-600">{Math.round(uploadProgress)}%</p>
             </div>
           )}
        </DialogContent>
       </Dialog>

      {/* Loader Global para Acciones */}
      {loadingAction && (
        <div className="fixed inset-0 bg-black/5 flex items-center justify-center z-[200] backdrop-blur-[1px]">
           <div className="bg-white p-6 rounded-3xl shadow-2xl flex flex-col items-center gap-4 animate-in zoom-in-95 duration-200 border border-gray-100">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
              <p className="text-sm font-black text-gray-900 uppercase tracking-widest">Preparando Editor...</p>
           </div>
        </div>
      )}

       <style jsx global>{`
        .custom-scrollbar-dark::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar-dark::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar-dark::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 10px; }
      `}</style>
    </div>
  )
}
